import React, { useState, useRef, useEffect, useMemo } from 'react';
import { appendCacheBuster, resolveElementStyle, buildBadgeStyle } from '../../utils/mobileStyleUtils';

export default function MobileVaultCarousel({
  coins: propCoins,
  cards: propCards,
  loading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  totalCount,
  isOperator = false,
  onInspectCardFullFrame,
  onSelectCard,
  onSelectCardForShop,
  onNavigateToShop,
  onGoToClaim,
  onRefresh,
  lastViewedCardCode,
  mobileStyles = null,
  cacheBusterKey = null
}) {
  const coins = useMemo(() => propCoins || propCards || [], [propCoins, propCards]);
  const handleInspect = onInspectCardFullFrame || onSelectCard;
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const pageKey = isOperator ? 'operator:vault' : 'end_user:vault';
  const resolvedBadges = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'badges'), [mobileStyles, pageKey]);
  const resolvedTypo = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'typography'), [mobileStyles, pageKey]);
  const customBadgeStyle = useMemo(() => buildBadgeStyle(resolvedBadges.style), [resolvedBadges]);
  const typoConfig = resolvedTypo.style || {};

  // Monitor scroll position to update active index indicator & auto-fetch more
  const handleScroll = () => {
    if (!scrollRef.current || coins.length === 0) return;
    const container = scrollRef.current;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.offsetWidth * 0.84; // 84% width per card
    const newIndex = Math.round(scrollLeft / itemWidth);
    if (newIndex >= 0 && newIndex < coins.length && newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
    // Auto trigger load more when approaching end of carousel
    if (hasMore && !loadingMore && onLoadMore) {
      const scrollEndThreshold = container.scrollWidth - container.offsetWidth - 80;
      if (scrollLeft >= scrollEndThreshold) {
        onLoadMore();
      }
    }
  };

  const scrollToCard = (index, smooth = false) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const cardEl = container.querySelector(`[data-testid="vault-card-slide-${index}"]`);
    if (cardEl) {
      const scrollPos = cardEl.offsetLeft - (container.offsetWidth - cardEl.offsetWidth) / 2;
      container.scrollTo({
        left: Math.max(0, scrollPos),
        behavior: smooth ? 'smooth' : 'auto'
      });
      setActiveIndex(index);
    } else {
      const itemWidth = container.offsetWidth * 0.84;
      container.scrollTo({
        left: index * itemWidth,
        behavior: smooth ? 'smooth' : 'auto'
      });
      setActiveIndex(index);
    }
  };

  // Scroll to last viewed card position when returning to carousel or mounting
  useEffect(() => {
    if (!lastViewedCardCode || coins.length === 0 || !scrollRef.current) return;
    const targetIdx = coins.findIndex(c => c.public_code === lastViewedCardCode || String(c.id) === String(lastViewedCardCode));
    if (targetIdx >= 0) {
      const timer = setTimeout(() => {
        scrollToCard(targetIdx, false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [lastViewedCardCode, coins]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        color: '#94a3b8',
        gap: '12px'
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid rgba(0, 198, 255, 0.2)',
          borderTopColor: '#00c6ff',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>
          {isOperator ? 'Loading Vault...' : 'Loading Personal Vault...'}
        </span>
      </div>
    );
  }

  if (!coins || coins.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '65vh',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          background: 'rgba(0, 198, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
          color: '#38bdf8'
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="12" cy="12" r="5" />
            <path d="M12 7v2" /><path d="M12 15v2" /><path d="M7 12h2" /><path d="M15 12h2" />
          </svg>
        </div>
        <h3 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px 0', color: '#f8fafc' }}>
          {isOperator ? 'No Allocated Cards' : 'Your Vault is Empty'}
        </h3>
        <p style={{ fontSize: '13px', color: '#94a3b8', maxWidth: '300px', lineHeight: 1.5, margin: '0 0 24px 0' }}>
          {isOperator
            ? 'No allocated trophy cards found for your account. Once cards are minted and issued, they will appear here.'
            : 'No trophy cards have been linked to your account yet. When an outfitter issues your card, it will appear here.'}
        </p>

        {!isOperator && onGoToClaim && (
          <button
            type="button"
            onClick={onGoToClaim}
            style={{
              background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '9999px',
              padding: '12px 24px',
              fontSize: '13px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 8px 24px rgba(0, 198, 255, 0.35)',
              cursor: 'pointer'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2l-2 2m-1-1l-3 3m5 5l-2-2m-1 1l-3-3" />
              <path d="M15 5l4 4-8.5 8.5H6.5v-4L15 5z" />
              <circle cx="7" cy="17" r="2" />
            </svg>
            <span>Claim Card with Code</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      paddingTop: '8px',
      paddingBottom: '90px' // room for floating bottom dock
    }}>
      {/* Header counter and quick stats */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px 4px 20px'
      }}>
        <span style={{
          fontSize: '18px',
          fontWeight: 800,
          color: typoConfig.heading_color || '#f8fafc',
          fontFamily: typoConfig.font_family
        }}>
          {isOperator ? 'Vault' : 'Personal Vault'}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!isOperator && (
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: typoConfig.body_color || '#94a3b8',
              fontFamily: typoConfig.font_family
            }}>
              {`${activeIndex + 1} of ${coins.length}`}
            </div>
          )}
          <span style={{
            ...customBadgeStyle,
            padding: '2px 8px',
            fontSize: '11px',
            fontFamily: typoConfig.font_family
          }}>
            {isOperator
              ? `${coins.length}${totalCount ? ` of ${totalCount}` : ''}`
              : `${coins.length} Card${coins.length > 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Swipeable Horizontal Scroll Container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '24px 16px 44px 16px',
          gap: '14px'
        }}
      >
        <style>{`div::-webkit-scrollbar { display: none; }`}</style>
        {coins.map((coin, idx) => {
          const cardTitle = coin.card_data?.species || coin.card_data?.title || coin.card_name || `Card #${coin.coin_number}`;
          const isSelected = activeIndex === idx;

          return (
            <div
              key={coin.id || idx}
              data-testid={`vault-card-slide-${idx}`}
              onClick={() => {
                setActiveIndex(idx);
                if (handleInspect) handleInspect(coin);
              }}
              style={{
                flex: '0 0 85%',
                maxWidth: '400px',
                scrollSnapAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
                padding: '4px 0',
                transition: 'transform 0.25s ease',
                transform: isSelected ? 'scale(1)' : 'scale(0.96)',
                opacity: isSelected ? 1 : 0.85
              }}
            >
              {/* Clean Card Surface */}
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '350 / 490',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  boxShadow: isSelected
                    ? '0 18px 32px -4px rgba(0, 0, 0, 0.55), 0 6px 14px -2px rgba(0, 0, 0, 0.35)'
                    : '0 8px 20px -2px rgba(0, 0, 0, 0.35)',
                  transition: 'box-shadow 0.25s ease'
                }}
              >
                {coin.preview_front_url ? (
                  <img
                    src={cacheBusterKey ? appendCacheBuster(coin.preview_front_url, cacheBusterKey) : coin.preview_front_url}
                    alt={cardTitle}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '16px',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🏆</div>
                    <div style={{ fontWeight: 700, color: '#e2e8f0' }}>{cardTitle}</div>
                    <div style={{ fontSize: '11px', marginTop: '4px' }}>#{coin.public_code}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Progressive Load More Card (at the end) */}
        {hasMore && (
          <div
            data-testid="vault-load-more-card"
            onClick={() => !loadingMore && onLoadMore && onLoadMore()}
            style={{
              flex: '0 0 75%',
              maxWidth: '340px',
              scrollSnapAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: '4px 0'
            }}
          >
            <div style={{
              width: '100%',
              aspectRatio: '350 / 490',
              borderRadius: '16px',
              border: '2px dashed rgba(0, 198, 255, 0.4)',
              background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.7) 0%, rgba(2, 6, 23, 0.85) 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '24px',
              boxSizing: 'border-box',
              textAlign: 'center',
              boxShadow: '0 12px 24px -4px rgba(0, 0, 0, 0.45)'
            }}>
              <div style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                background: 'rgba(0, 198, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#38bdf8'
              }}>
                {loadingMore ? (
                  <div style={{
                    width: '26px',
                    height: '26px',
                    border: '3px solid rgba(0, 198, 255, 0.2)',
                    borderTopColor: '#00c6ff',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                )}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: '#f8fafc' }}>
                  {loadingMore ? 'Loading Cards...' : 'Load More Cards'}
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  Showing {coins.length} of {totalCount || coins.length} allocated cards
                </div>
              </div>
              {!loadingMore && (
                <button
                  type="button"
                  style={{
                    background: typoConfig.accent_color ? `linear-gradient(135deg, ${typoConfig.accent_color} 0%, #0072ff 100%)` : 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '8px 18px',
                    fontSize: '12px',
                    fontWeight: 700,
                    marginTop: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 198, 255, 0.3)'
                  }}
                >
                  Load Next Batch
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pagination Dot Indicators */}
      {coins.length > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          marginTop: '-20px',
          position: 'relative',
          zIndex: 10
        }}>
          {coins.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => scrollToCard(idx)}
              style={{
                width: activeIndex === idx ? '20px' : '6px',
                height: '6px',
                borderRadius: '3px',
                background: activeIndex === idx ? '#00c6ff' : 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'all 0.25s ease'
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
